import React from 'react';
import PropTypes from 'prop-types';
import { LuChevronRight } from 'react-icons/lu';

// Colour of the file tile by kind
export const KIND_TONES = {
  pdf: 'tw-bg-rose-500/10 tw-text-rose-600 dark:tw-text-rose-300',
  video: 'tw-bg-violet-500/10 tw-text-violet-600 dark:tw-text-violet-300',
  ppt: 'tw-bg-orange-500/10 tw-text-orange-600 dark:tw-text-orange-300',
  word: 'tw-bg-blue-500/10 tw-text-blue-600 dark:tw-text-blue-300',
  form: 'tw-bg-sky-500/10 tw-text-sky-600 dark:tw-text-sky-300',
  other: 'tw-bg-slate-500/10 tw-text-slate-600 dark:tw-text-slate-300',
};

/**
 * One document in a Files list: file tile, name, a meta line and badges.
 * The whole row is the button; `trailing` holds extra controls (e.g. download)
 * that sit beside it rather than inside it.
 */
const DocumentRow = ({ icon: Icon, tone, title, meta, badges, onClick, disabled, emphasis, trailing, trailingIcon: TrailingIcon = LuChevronRight }) => (
  <li className="tw-flex tw-items-stretch tw-gap-1.5">
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`tw-group tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-p-2.5 tw-text-left tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 disabled:tw-cursor-not-allowed disabled:tw-opacity-55 sm:tw-p-3 ${
        emphasis
          ? 'tw-border-amber-400/40 tw-bg-amber-50/70 hover:tw-bg-amber-50 dark:tw-border-amber-400/25 dark:tw-bg-amber-400/[0.06] dark:hover:tw-bg-amber-400/[0.1]'
          : 'tw-border-slate-200 tw-bg-white/70 hover:tw-border-teal-400/40 hover:tw-bg-white dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:hover:tw-border-teal-400/30 dark:hover:tw-bg-white/[0.06]'
      }`}
    >
      <span className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-xl ${tone}`}>
        <Icon aria-hidden="true" className="tw-h-5 tw-w-5" />
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-line-clamp-2 tw-break-words tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">{title}</span>
        {meta && <span className="tw-mt-0.5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{meta}</span>}
      </span>
      {badges && <span className="tw-flex tw-shrink-0 tw-flex-col tw-items-end tw-gap-1 sm:tw-flex-row sm:tw-items-center">{badges}</span>}
      {!disabled && <TrailingIcon aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-300 tw-transition-transform group-hover:tw-translate-x-0.5 group-hover:tw-text-teal-500 dark:tw-text-slate-600" />}
    </button>
    {trailing}
  </li>
);

DocumentRow.propTypes = {
  icon: PropTypes.elementType.isRequired,
  tone: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  meta: PropTypes.node,
  badges: PropTypes.node,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  emphasis: PropTypes.bool,
  trailing: PropTypes.node,
  trailingIcon: PropTypes.elementType,
};

export default DocumentRow;
